import { ProposalTemplate } from './templates'
import { Block } from './types'
import { X, Check, ArrowRight, Star, CheckCircle, ChevronDown } from 'lucide-react'
import { useLocale } from '../../contexts/LocaleContext'

interface TemplatePreviewModalProps {
  template: ProposalTemplate
  onClose: () => void
  onConfirm: () => void
}

export default function TemplatePreviewModal({ template, onClose, onConfirm }: TemplatePreviewModalProps) {
  const { tx, isRTL } = useLocale()
  const defaultAlign = isRTL ? 'right' : 'left'

  const renderBlock = (block: Block) => {
    switch (block.type) {
      case 'cover': {
        const coverTextAlignment = block.textAlignment || defaultAlign
        const coverSize = block.coverSize || 'top'
        const overlayOpacity = block.overlayOpacity ?? 40
        return (
          <div
            key={block.id}
            className={`relative rounded-xl overflow-hidden flex ${coverSize === 'full' ? 'min-h-[400px]' : 'min-h-[200px]'}`}
            style={{
              backgroundImage: block.backgroundImage ? `url(${block.backgroundImage})` : undefined,
              backgroundColor: block.backgroundColor || '#1e40af',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            {block.overlay && (
              <div className="absolute inset-0" style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity / 100})` }} />
            )}
            {block.logoUrl && (
              <div className="absolute z-20 top-4 right-4">
                <img src={block.logoUrl} alt="Logo" className="w-20 h-20 object-contain bg-white/95 rounded-xl p-2 shadow-lg" />
              </div>
            )}
            <div className={`relative z-10 flex-1 flex flex-col p-6 ${
              coverTextAlignment === 'center' ? 'items-center text-center' :
              coverTextAlignment === 'left' ? 'items-end text-left' :
              'items-start text-right'
            }`}>
              <h1 className="text-3xl font-bold text-white mb-2">{block.title || tx('Heading', 'כותרת')}</h1>
              {block.subtitle && <p className="text-lg text-white/90">{block.subtitle}</p>}
              {block.preparedFor !== undefined && (
                <div className="text-white/90 mt-2">
                  <span className="text-sm opacity-80">{tx('Prepared for:', 'הוכן עבור:')}</span>
                  <p className="text-xl font-semibold">{block.clientName || tx('Client Name', 'שם הלקוח')}</p>
                </div>
              )}
            </div>
          </div>
        )
      }

      case 'heading': {
        const HeadingTag = `h${block.level}` as keyof JSX.IntrinsicElements
        const headingSizes = { 1: 'text-2xl', 2: 'text-xl', 3: 'text-lg' }
        return (
          <HeadingTag
            key={block.id}
            className={`${headingSizes[block.level]} font-bold`}
            style={{ textAlign: block.alignment || defaultAlign, color: block.color || '#0f172a' }}
          >
            {block.content || tx('Heading', 'כותרת')}
          </HeadingTag>
        )
      }

      case 'text': {
        const textSizeClasses = { small: 'text-xs', normal: 'text-sm', large: 'text-base', xlarge: 'text-lg' }
        return (
          <div key={block.id}>
            {block.title && <h3 className="text-base font-bold mb-1" style={{ color: block.color || '#1e293b' }}>{block.title}</h3>}
            <p
              className={`leading-relaxed whitespace-pre-wrap ${textSizeClasses[block.fontSize || 'normal']}`}
              style={{ textAlign: block.alignment || defaultAlign, color: block.color || '#334155' }}
            >
              {block.content || tx('Text...', 'טקסט...')}
            </p>
          </div>
        )
      }

      case 'image':
        if (!block.src) return null
        return (
          <div key={block.id}>
            <img src={block.src} alt={block.alt || ''} className="w-full rounded-lg" />
          </div>
        )

      case 'quote':
        return (
          <blockquote key={block.id} className="border-r-4 border-blue-500 pr-4 py-2 bg-slate-50 rounded-l">
            <p className="italic text-slate-600">{block.content || tx('Quote...', 'ציטוט...')}</p>
            {block.author && <footer className="text-sm text-slate-500 mt-1">— {block.author}</footer>}
          </blockquote>
        )

      case 'list':
        return (
          <ul key={block.id} className={`${block.style === 'numbered' ? 'list-decimal' : block.style === 'check' ? 'list-none' : 'list-disc'} pr-6 space-y-1`}>
            {block.items.map((item, i) => (
              <li key={i} className="text-slate-700 text-sm">
                {block.style === 'check' && <span className="text-green-500 ml-2">✓</span>}
                {item || tx('Item', 'פריט')}
              </li>
            ))}
          </ul>
        )

      case 'price_table': {
        if (!block.items.length) return null
        const total = block.items.reduce((sum, item) => sum + item.total, 0)
        return (
          <div key={block.id} className="overflow-hidden rounded-xl border border-slate-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100">
                  <th className="p-2 text-right font-medium">{tx('Description', 'תיאור')}</th>
                  {block.showQuantity && <th className="p-2 text-center font-medium w-16">{tx('Qty', 'כמות')}</th>}
                  {block.showUnitPrice && <th className="p-2 text-center font-medium w-20">{tx('Price', 'מחיר')}</th>}
                  <th className="p-2 text-left font-medium w-20">{tx('Total', 'סה"כ')}</th>
                </tr>
              </thead>
              <tbody>
                {block.items.map((item) => (
                  <tr key={item.id} className="border-t border-slate-100">
                    <td className="p-2">{item.description || tx('Item', 'פריט')}</td>
                    {block.showQuantity && <td className="p-2 text-center">{item.quantity}</td>}
                    {block.showUnitPrice && <td className="p-2 text-center">₪{item.unitPrice.toLocaleString()}</td>}
                    <td className="p-2 text-left font-medium">₪{item.total.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-blue-600 text-white">
                  <td colSpan={1 + (block.showQuantity ? 1 : 0) + (block.showUnitPrice ? 1 : 0)} className="p-2 font-medium">{tx('Total Due', 'סה"כ לתשלום')}</td>
                  <td className="p-2 text-left font-bold">₪{total.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      }

      case 'divider':
        return <hr key={block.id} className={`border-t-2 border-slate-200 my-2 ${block.style === 'dashed' ? 'border-dashed' : block.style === 'dotted' ? 'border-dotted' : 'border-solid'}`} />

      case 'spacer':
        return <div key={block.id} className={block.height === 'small' ? 'h-4' : block.height === 'large' ? 'h-12' : 'h-8'} />

      case 'signature':
        return (
          <div key={block.id} className="p-4 border-2 border-dashed border-slate-300 rounded-xl text-center bg-slate-50">
            <p className="text-slate-500 text-sm mb-2">{block.label}</p>
            <div className="w-40 mx-auto border-b-2 border-slate-400 mb-1" />
            <p className="text-xs text-slate-400">{tx('Sign here', 'חתום כאן')}</p>
          </div>
        )

      case 'terms': {
        const termsSizeClass = block.textSize === 'small' ? 'text-xs' : block.textSize === 'large' ? 'text-sm' : 'text-xs'
        return (
          <div key={block.id} className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <h3 className="font-bold text-blue-800 mb-3 text-base">{block.title || tx('Terms', 'תנאים')}</h3>
            <ul className="space-y-1.5">
              {block.items.map((item, i) => (
                <li key={i} className={`flex items-start gap-2 text-slate-700 ${termsSizeClass}`}>
                  {block.style === 'check' && <span className="text-blue-500 mt-0.5">✓</span>}
                  {block.style === 'numbered' && <span className="text-blue-600 font-medium">{i + 1}.</span>}
                  {block.style === 'simple' && <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0" />}
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )
      }

      case 'dual_signature':
        return (
          <div key={block.id} className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-xl">
            <div className="border-2 border-dashed border-slate-300 rounded-xl p-4 bg-white">
              <p className="text-slate-500 text-xs mb-3">{block.rightLabel || tx('Client Signature', 'חתימת הלקוח')}</p>
              <div className="h-14 border-b-2 border-slate-300 mb-1" />
              {block.rightName && <p className="text-xs text-slate-600 text-center">{block.rightName}</p>}
            </div>
            <div className="border-2 border-dashed border-slate-300 rounded-xl p-4 bg-white">
              <p className="text-slate-500 text-xs mb-3">{block.leftLabel || tx('Service Provider Signature', 'חתימת נותן השירות')}</p>
              <div className="h-14 border-b-2 border-slate-300 mb-1" />
              {block.leftName && <p className="text-xs text-slate-600 text-center">{block.leftName}</p>}
            </div>
          </div>
        )

      case 'benefits':
        return (
          <div key={block.id} className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
            <h3 className="text-base font-bold text-emerald-800 mb-3">{block.title || tx('Benefits', 'יתרונות')}</h3>
            <div className={`grid ${block.columns === 2 ? 'grid-cols-2' : 'grid-cols-1'} gap-2`}>
              {block.items.map((item, i) => (
                <div key={i} className="flex items-center gap-2 bg-white rounded-lg p-2 shadow-sm">
                  <span className="flex-shrink-0 text-sm" style={{ color: block.iconColor || '#10b981' }}>✓</span>
                  <span className="text-slate-700 text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        )

      case 'gallery': {
        if (!block.images.length) return null
        const colsClass: Record<number, string> = { 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' }
        return (
          <div key={block.id} className={`grid ${colsClass[block.columns]} gap-2`}>
            {block.images.map((img, i) => (
              <img key={i} src={img.src} alt={img.alt || ''} className="w-full rounded-lg" />
            ))}
          </div>
        )
      }

      case 'video':
        return (
          <div key={block.id} className="p-4 bg-slate-100 rounded-xl text-center">
            <span className="text-2xl">🎬</span>
            <p className="text-xs text-slate-500 mt-1">{tx('Video', 'וידאו')}</p>
          </div>
        )

      case 'timeline':
        return (
          <div key={block.id} className="py-2">
            {block.title && <h3 className="text-lg font-bold text-slate-800 mb-4">{block.title}</h3>}
            {block.layout === 'horizontal' ? (
              <div className="flex overflow-x-auto gap-3 pb-2">
                {block.items.map((item, i) => (
                  <div key={item.id} className="flex-shrink-0 w-48">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: block.color || '#FF69B4' }}>{i + 1}</div>
                      <div className="flex-1 h-0.5 rounded" style={{ backgroundColor: block.color || '#FF69B4', opacity: 0.3 }} />
                    </div>
                    <h4 className="font-semibold text-slate-800 text-sm">{item.title}</h4>
                    {item.description && <p className="text-xs text-slate-600 mt-0.5">{item.description}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="relative">
                <div className="absolute right-4 top-0 bottom-0 w-0.5" style={{ backgroundColor: block.color || '#FF69B4', opacity: 0.3 }} />
                <div className="space-y-4">
                  {block.items.map((item, i) => (
                    <div key={item.id} className="relative flex gap-3 pr-1">
                      <div className="relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0" style={{ backgroundColor: block.color || '#FF69B4' }}>{i + 1}</div>
                      <div className="flex-1 bg-slate-50 rounded-xl p-3">
                        <h4 className="font-semibold text-slate-800 text-sm">{item.title}</h4>
                        {item.description && <p className="text-xs text-slate-600 mt-0.5">{item.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )

      case 'team': {
        const teamColsClass: Record<number, string> = { 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' }
        return (
          <div key={block.id} className="py-2">
            {block.title && <h3 className="text-lg font-bold text-slate-800 mb-4">{block.title}</h3>}
            <div className={`grid ${teamColsClass[block.columns]} gap-4`}>
              {block.members.map((member) => (
                <div key={member.id} className="text-center">
                  {member.image ? (
                    <img src={member.image} alt={member.name} className="w-16 h-16 rounded-full object-cover mx-auto mb-2" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-accent-500 flex items-center justify-center text-white text-xl font-bold mx-auto mb-2">{member.name.charAt(0)}</div>
                  )}
                  <h4 className="font-semibold text-slate-800 text-sm">{member.name}</h4>
                  <p className="text-xs text-slate-700">{member.role}</p>
                </div>
              ))}
            </div>
          </div>
        )
      }

      case 'testimonials':
        return (
          <div key={block.id} className="py-2">
            {block.title && <h3 className="text-lg font-bold text-slate-800 mb-4">{block.title}</h3>}
            {block.layout === 'cards' ? (
              <div className="grid md:grid-cols-2 gap-3">
                {block.items.map((item) => (
                  <div key={item.id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                    {item.rating && (
                      <div className="flex gap-0.5 mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-3 h-3 ${i < item.rating! ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                        ))}
                      </div>
                    )}
                    <p className="text-slate-600 italic text-sm mb-2">"{item.content}"</p>
                    <p className="text-xs font-medium text-slate-800">— {item.author}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {block.items.map((item) => (
                  <div key={item.id} className="border-r-4 border-slate-500 pr-3 py-1">
                    <p className="text-slate-600 italic text-sm mb-1">"{item.content}"</p>
                    <p className="text-xs text-slate-500">— {item.author}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )

      case 'packages':
        return (
          <div key={block.id} className="py-2">
            {block.title && <h3 className="text-lg font-bold text-slate-800 mb-4 text-center">{block.title}</h3>}
            <div className={`grid ${({1:'grid-cols-1',2:'grid-cols-2',3:'grid-cols-3',4:'grid-cols-4'} as Record<number,string>)[block.packages.length] || 'grid-cols-1'} gap-3`}>
              {block.packages.map((pkg) => (
                <div
                  key={pkg.id}
                  className={`rounded-xl border-2 p-4 ${pkg.highlighted ? 'border-slate-500 bg-slate-50 shadow-lg' : 'border-slate-200 bg-white'}`}
                >
                  {pkg.highlighted && <div className="text-xs font-bold text-slate-700 mb-1">{tx('Recommended', 'מומלץ')}</div>}
                  <h4 className="text-base font-bold text-slate-800">{pkg.name}</h4>
                  <div className="my-2">
                    <span className="text-2xl font-bold text-slate-900">{pkg.currency}{pkg.price.toLocaleString()}</span>
                    {pkg.period && <span className="text-slate-500 text-xs">/{pkg.period}</span>}
                  </div>
                  <ul className="space-y-1">
                    {pkg.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-1.5 text-xs text-slate-600">
                        <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )

      case 'faq':
        return (
          <div key={block.id} className="py-2">
            {block.title && <h3 className="text-lg font-bold text-slate-800 mb-4">{block.title}</h3>}
            <div className="space-y-2">
              {block.items.map((item) => (
                <details key={item.id} className="group bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50 list-none text-sm">
                    <span className="font-medium text-slate-800">{item.question}</span>
                    <ChevronDown className="w-4 h-4 text-slate-400 group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="px-3 pb-3 pt-1 text-slate-600 border-t border-slate-100 text-sm">{item.answer}</div>
                </details>
              ))}
            </div>
          </div>
        )

      case 'client_logos': {
        const logoColsClass: Record<number, string> = { 3: 'grid-cols-3', 4: 'grid-cols-4', 5: 'grid-cols-5', 6: 'grid-cols-6' }
        return (
          <div key={block.id} className="py-2">
            {block.title && <h3 className="text-lg font-bold text-slate-800 mb-4 text-center">{block.title}</h3>}
            <div className={`grid ${logoColsClass[block.columns]} gap-4 items-center`}>
              {block.logos.map((logo) => (
                <div key={logo.id} className="flex items-center justify-center p-3">
                  <img src={logo.src} alt={logo.alt} className={`max-h-12 object-contain ${block.grayscale ? 'grayscale' : ''}`} />
                </div>
              ))}
            </div>
          </div>
        )
      }

      case 'stats': {
        const statsColsClass: Record<number, string> = { 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' }
        return (
          <div key={block.id} className="py-2">
            {block.title && <h3 className="text-lg font-bold text-slate-800 mb-4 text-center">{block.title}</h3>}
            <div className={`grid ${statsColsClass[block.columns]} gap-4`}>
              {block.items.map((item) => (
                <div key={item.id} className="text-center p-3 bg-slate-50 rounded-xl">
                  <div className="text-2xl font-bold mb-1" style={{ color: block.color || '#FF69B4' }}>{item.value}</div>
                  <div className="text-slate-600 text-xs">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        )
      }

      default:
        return null
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4" dir="ltr">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b border-slate-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between bg-white flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors text-slate-400 hover:text-slate-600 flex-shrink-0"
            >
              <ArrowRight className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-bold text-slate-900 truncate">{template.nameHe}</h2>
              <p className="text-xs text-slate-500 truncate">{template.descriptionHe}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors text-slate-400 hover:text-slate-600 flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-y-auto bg-slate-100 p-3 sm:p-6">
          <div data-keep-light className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm p-4 sm:p-6 space-y-4 sm:space-y-5">
            {template.blocks.map((block) => renderBlock(block))}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between bg-white flex-shrink-0">
          <div className="text-xs sm:text-sm text-slate-500">
            {template.blocks.length} {tx('blocks', 'בלוקים')}
          </div>
          <div className="flex gap-2 sm:gap-3">
            <button
              onClick={onClose}
              className="px-3 sm:px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors font-medium"
            >
              {tx('Back', 'חזרה')}
            </button>
            <button
              onClick={onConfirm}
              className="px-4 sm:px-6 py-2 text-sm text-white bg-slate-700 hover:bg-slate-800 rounded-lg transition-all flex items-center gap-1.5 sm:gap-2 font-medium shadow-sm"
            >
              <Check className="w-4 h-4" />
              {tx('Use Template', 'השתמש בתבנית')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
